import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiDownload } = FiIcons;

const LeadReportModal = ({ lead, onClose }) => {
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeContactHistory, setIncludeContactHistory] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (includeContactHistory) {
      fetchContacts();
    }
  }, [includeContactHistory, lead.id]);

  const fetchContacts = async () => {
    if (!lead.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('lead_id', lead.id)
        .order('contact_date', { ascending: false });
      
      if (error) throw error;
      setContacts(data || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('Lead Report', 20, 20);
    
    // Lead Information
    doc.setFontSize(16);
    doc.text('Lead Information', 20, 40);
    
    const leadInfo = [
      ['Name', lead.name],
      ['Company', lead.company_name || 'N/A'],
      ['Phone', lead.phone],
      ['Email', lead.email || 'N/A'],
      ['Website', lead.website || 'N/A'],
      ['Service Requested', lead.service_requested || 'N/A'],
      ['Lead Source', lead.lead_source || 'N/A'],
      ['Status', lead.status],
      ['Created Date', format(new Date(lead.created_at), 'MMM d, yyyy')],
      ['Updated Date', format(new Date(lead.updated_at), 'MMM d, yyyy')]
    ];
    
    doc.autoTable({
      startY: 50,
      head: [['Field', 'Value']],
      body: leadInfo,
      theme: 'grid'
    });
    
    let yPosition = doc.lastAutoTable.finalY + 20;
    
    // Notes
    if (includeNotes && (lead.notes || lead.ai_reference_notes)) {
      doc.setFontSize(16);
      doc.text('Notes', 20, yPosition);
      yPosition += 10;
      
      if (lead.notes) {
        doc.setFontSize(12);
        doc.text('Private Notes:', 20, yPosition);
        yPosition += 5;
        const splitNotes = doc.splitTextToSize(lead.notes, 170);
        doc.text(splitNotes, 20, yPosition);
        yPosition += splitNotes.length * 5 + 10;
      }
      
      if (lead.ai_reference_notes) {
        doc.setFontSize(12);
        doc.text('AI Reference Notes:', 20, yPosition);
        yPosition += 5;
        const splitAINotes = doc.splitTextToSize(lead.ai_reference_notes, 170);
        doc.text(splitAINotes, 20, yPosition);
        yPosition += splitAINotes.length * 5 + 10;
      }
    }
    
    // Contact Log History
    if (includeContactHistory && contacts.length > 0) {
      // Add a page break if we're running out of space
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(16);
      doc.text('Contact Log History', 20, yPosition);
      yPosition += 10;
      
      // Process each contact separately to handle the full content
      contacts.forEach((contact, index) => {
        // Add a new page if we're getting close to the bottom
        if (yPosition > 230) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Contact header (date, type, subject)
        const contactDate = format(new Date(contact.contact_date), 'MMM d, yyyy');
        const contactType = contact.contact_type === 'phone' ? 'Phone Call' : 
                           contact.contact_type === 'email' ? 'Email' : 'SMS';
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${contactDate} - ${contactType}`, 20, yPosition);
        yPosition += 6;
        
        if (contact.subject) {
          doc.setFont(undefined, 'normal');
          doc.text(`Subject: ${contact.subject}`, 25, yPosition);
          yPosition += 6;
        }
        
        // Full content
        if (contact.content) {
          doc.setFont(undefined, 'normal');
          const splitContent = doc.splitTextToSize(contact.content, 165);
          doc.text(splitContent, 25, yPosition);
          yPosition += splitContent.length * 5 + 3;
        }
        
        // Notes if available
        if (contact.notes) {
          doc.setFont(undefined, 'italic');
          doc.text('Notes:', 25, yPosition);
          yPosition += 5;
          
          doc.setFont(undefined, 'normal');
          const splitNotes = doc.splitTextToSize(contact.notes, 160);
          doc.text(splitNotes, 30, yPosition);
          yPosition += splitNotes.length * 5 + 3;
        }
        
        // Add separator line between contacts
        if (index < contacts.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.line(20, yPosition, 190, yPosition);
          yPosition += 10;
        }
      });
    }
    
    // Save the PDF
    doc.save(`lead-report-${lead.name.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    
    // Close the modal after generating
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Generate Report</h2>
          <button 
            onClick={() => {
              if (typeof onClose === 'function') {
                onClose();
              }
            }} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <p className="text-gray-600">
            Generate a PDF report for <strong>{lead.name}</strong>
            {lead.company_name && ` from ${lead.company_name}`}
          </p>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="includeNotes"
                type="checkbox"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
                className="h-4 w-4 text-electric-600 focus:ring-electric-500 border-gray-300 rounded"
              />
              <label htmlFor="includeNotes" className="ml-2 text-sm text-gray-700">
                Include notes in report
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="includeContactHistory"
                type="checkbox"
                checked={includeContactHistory}
                onChange={(e) => setIncludeContactHistory(e.target.checked)}
                className="h-4 w-4 text-electric-600 focus:ring-electric-500 border-gray-300 rounded"
              />
              <label htmlFor="includeContactHistory" className="ml-2 text-sm text-gray-700">
                Include contact log history
              </label>
              {loading && (
                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-electric-600"></div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6">
          <button 
            onClick={() => {
              if (typeof onClose === 'function') {
                onClose();
              }
            }} 
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            onClick={generatePDF} 
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <SafeIcon icon={FiDownload} className="h-4 w-4" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadReportModal;